"use client";

import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import type { MailboxMessage } from "./stores/useMailboxStore";

export type MailboxFilterType = "all" | "unread" | "support";

export interface MailRow {
  id: string;
  from: string;
  to: string;
  subject: string;
  preview: string;
  body: string;
  date: string;
  time: string;
  isUnread: boolean;
  isSupport: boolean;
  isSent: boolean;
  severity: "default" | "warning" | "highPriority";
}

export function formatSentAt(unix: number): { date: string; time: string } {
  if (unix <= 0) return { date: "—", time: "—" };
  const d = new Date(unix * 1000);
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
}

export function displayParty(party: string): string {
  if (party === "Support") return "Support";
  const login = party.split(" ")[0];
  return login || party;
}

export function toMailRow(
  m: MailboxMessage,
  activeAccountKey: string | null
): MailRow {
  const { date, time } = formatSentAt(m.sentAtUnix);
  const isSupport = m.from === "Support" || m.to === "Support";
  const isSent = !!activeAccountKey && m.from === activeAccountKey;
  return {
    id: m.messageId,
    from: m.from,
    to: m.to,
    subject: m.subject || "(No subject)",
    preview: m.body.length > 100 ? `${m.body.slice(0, 100)}…` : m.body,
    body: m.body,
    date,
    time,
    isUnread: m.isUserUnread,
    isSupport,
    isSent,
    severity: m.severity,
  };
}

export function filterMailRows(
  rows: MailRow[],
  filterType: MailboxFilterType,
  searchQuery: string
): MailRow[] {
  let list = [...rows];
  const q = searchQuery.trim().toLowerCase();

  if (filterType === "unread") list = list.filter((r) => r.isUnread);
  if (filterType === "support") list = list.filter((r) => r.isSupport);

  if (q) {
    list = list.filter(
      (r) =>
        r.subject.toLowerCase().includes(q) ||
        r.preview.toLowerCase().includes(q) ||
        r.from.toLowerCase().includes(q) ||
        r.to.toLowerCase().includes(q)
    );
  }

  return list;
}

export function listPartyLabel(row: MailRow): string {
  return row.isSent ? `To ${displayParty(row.to)}` : displayParty(row.from);
}

export function DirectionIcon({
  isSent,
  className = "h-3.5 w-3.5",
}: {
  isSent: boolean;
  className?: string;
}) {
  const Icon = isSent ? ArrowUpRight : ArrowDownLeft;
  return (
    <Icon
      className={`shrink-0 ${className} ${
        isSent ? "text-muted-foreground" : "text-primary"
      }`}
      aria-hidden
    />
  );
}

export function SeverityMark({ severity }: { severity: MailRow["severity"] }) {
  if (severity === "default") return null;
  return (
    <span
      className={
        severity === "highPriority"
          ? "text-destructive text-xs font-bold"
          : "text-amber-500 dark:text-amber-400 text-xs font-bold"
      }
      title={severity === "highPriority" ? "High priority" : "Warning"}
    >
      !
    </span>
  );
}
